import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { config } from "../core";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from "@material-ui/core";
import axios from "axios";

const FEEDBACK_TYPES = [
  "False Positive",
  "False Negative",
  "Missed Issue",
  "Wrong Severity",
  "Correct",
];

const QAReview = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(50);
  const [expandedRow, setExpandedRow] = useState(null);
  const [detailData, setDetailData] = useState({});
  const [detailLoading, setDetailLoading] = useState({});
  const [completeDialog, setCompleteDialog] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackType, setFeedbackType] = useState("");
  const [feedbackText, setFeedbackText] = useState("");
  const [qaResult, setQaResult] = useState("");
  // Per-policy suggested-edit selections: { [policyNo]: { [answerKey]: { accepted, value } } }
  const [qaEdits, setQaEdits] = useState({});
  const [applyingEdits, setApplyingEdits] = useState({});
  const [flaggingField, setFlaggingField] = useState({});

  const { data, error } = useSWR(
    `${config.api.baseUrl}/qa/scores?limit=${rowsPerPage}&offset=${page * rowsPerPage}`,
    { refreshInterval: 0 }
  );

  const items = data?.result?.items || [];
  const total = data?.result?.total || 0;

  const loadDetail = async (policyNo, force) => {
    if (detailData[policyNo] && !force) {return;}
    setDetailLoading((prev) => ({ ...prev, [policyNo]: true }));
    try {
      const res = await axios.get(`${config.api.baseUrl}/qa/scores/${policyNo}`);
      setDetailData((prev) => ({ ...prev, [policyNo]: res.data.result }));
    } catch (e) {
      console.error("Failed to load QA detail", e);
    }
    setDetailLoading((prev) => ({ ...prev, [policyNo]: false }));
  };

  const toggleRow = (policyNo) => {
    if (expandedRow === policyNo) {
      setExpandedRow(null);
    } else {
      setExpandedRow(policyNo);
      loadDetail(policyNo);
    }
  };

  const openCompleteDialog = (policyNo) => {
    setCompleteDialog(policyNo);
    setFeedbackType("");
    setFeedbackText("");
    setQaResult("");
  };

  const closeCompleteDialog = () => {
    setCompleteDialog(null);
    setFeedbackType("");
    setFeedbackText("");
    setQaResult("");
  };

  const submitQA = async () => {
    if (!completeDialog || !qaResult) {return;}
    setSubmitting(true);
    try {
      await axios.post(`${config.api.baseUrl}/qa/complete`, {
        policyNo: completeDialog,
        result: qaResult,
        feedbackType: feedbackType,
        feedbackText: feedbackText,
      });
      // Refresh the scores list
      mutate(`${config.api.baseUrl}/qa/scores?limit=${rowsPerPage}&offset=${page * rowsPerPage}`);
      // Clear detail cache so it re-fetches
      setDetailData((prev) => {
        const next = { ...prev };
        delete next[completeDialog];
        return next;
      });
      closeCompleteDialog();
    } catch (e) {
      console.error("Failed to submit QA", e);
    }
    setSubmitting(false);
  };

  // --- Suggested edits ---
  const editStateFor = (policyNo, edit) => {
    const pe = qaEdits[policyNo] && qaEdits[policyNo][edit.answerKey];
    if (pe) {return pe;}
    return { accepted: true, value: edit.suggestedValue };
  };

  const setEditState = (policyNo, answerKey, patch) => {
    setQaEdits((prev) => {
      const next = { ...prev };
      const pe = { ...next[policyNo] && next[policyNo][answerKey] ? next[policyNo][answerKey] : { accepted: true, value: "" } };
      Object.assign(pe, patch);
      next[policyNo] = { ...next[policyNo] || {}, [answerKey]: pe };
      return next;
    });
  };

  const applyEdits = async (policyNo) => {
    const detail = detailData[policyNo];
    if (!detail || !detail.suggestedEdits || detail.suggestedEdits.length === 0) {return;}
    const edits = detail.suggestedEdits
      .map((e) => ({ ...e, _state: editStateFor(policyNo, e) }))
      .filter((e) => e._state.accepted)
      .map((e) => ({ answerKey: e.answerKey, newValue: e._state.value, reason: e.reason }));
    if (edits.length === 0) {return;}
    setApplyingEdits((prev) => ({ ...prev, [policyNo]: true }));
    try {
      await axios.post(`${config.api.baseUrl}/qa/scores/${policyNo}/apply-edits`, { edits });
      // Clear detail cache so it re-fetches, and drop the edit selections.
      setDetailData((prev) => { const n = { ...prev }; delete n[policyNo]; return n; });
      setQaEdits((prev) => { const n = { ...prev }; delete n[policyNo]; return n; });
      mutate(`${config.api.baseUrl}/qa/scores?limit=${rowsPerPage}&offset=${page * rowsPerPage}`);
      if (expandedRow === policyNo) { loadDetail(policyNo, true); }
    } catch (e) {
      console.error("Failed to apply QA edits", e);
    }
    setApplyingEdits((prev) => ({ ...prev, [policyNo]: false }));
  };

  const flagField = async (policyNo, field) => {
    if (!window.confirm(`Flag ${field}? This writes to the inspection answers.`)) {return;}
    setFlaggingField((prev) => ({ ...prev, [`${policyNo}:${field}`]: true }));
    try {
      await axios.post(`${config.api.baseUrl}/qa/scores/${policyNo}/apply-edits`, { flags: [field] });
      setDetailData((prev) => { const n = { ...prev }; delete n[policyNo]; return n; });
      mutate(`${config.api.baseUrl}/qa/scores?limit=${rowsPerPage}&offset=${page * rowsPerPage}`);
      if (expandedRow === policyNo) { loadDetail(policyNo, true); }
    } catch (e) {
      console.error("Failed to flag field", e);
    }
    setFlaggingField((prev) => ({ ...prev, [`${policyNo}:${field}`]: false }));
  };

  const scoreColor = (score) => {
    if (score >= 4.0) {return "tw-text-green-600";}
    if (score >= 3.5) {return "tw-text-green-500";}
    if (score >= 3.0) {return "tw-text-yellow-600";}
    if (score >= 2.0) {return "tw-text-orange-600";}
    return "tw-text-red-600";
  };

  const statusBadge = (status) => {
    switch (status) {
    case "passed":
      return <span className="tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-semibold tw-bg-green-100 tw-text-green-800">Passed</span>;
    case "failed":
      return <span className="tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-semibold tw-bg-red-100 tw-text-red-800">Failed</span>;
    default:
      return <span className="tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-semibold tw-bg-yellow-100 tw-text-yellow-800">Pending</span>;
    }
  };

  return (
    <div className="tw-p-4">
      <h1 className="tw-text-2xl tw-font-bold tw-mb-6">QA Review</h1>

      {!data && !error && 
        <div className="tw-text-center tw-py-12">
          <CircularProgress size={32} />
        </div>
      }

      {error && 
        <div className="tw-text-center tw-py-12 tw-text-red-600">
          Error loading QA scores. Make sure you have manager access.
        </div>
      }

      {data && 
        <>
          <div className="tw-mb-4 tw-text-sm tw-text-gray-600">
            Showing {items.length} of {total} scored inspections
          </div>

          {items.length === 0 ? 
            <div className="tw-text-center tw-py-12 tw-text-gray-500">
              No scored inspections found. The QA scoring agent needs to process inspections first.
            </div>
            : 
            <div className="tw-overflow-x-auto">
              <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
                <thead>
                  <tr className="tw-bg-fs-dark-blue tw-bg-opacity-25">
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-w-8"></th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Policy #</th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Address</th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Inspector</th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Score</th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Auto-Fail</th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Review</th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">QA Status</th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="tw-divide-y tw-divide-gray-200">
                  {items.map((item, i) => {
                    const isExpanded = expandedRow === item.policyNo;
                    const detail = detailData[item.policyNo];
                    const isLoading = detailLoading[item.policyNo];
                    return (
                      <React.Fragment key={item.policyNo}>
                        <tr className={i % 2 ? "tw-bg-gray-100" : "tw-bg-white"}>
                          <td className="tw-px-4 tw-py-2 tw-cursor-pointer" onClick={() => toggleRow(item.policyNo)}>
                            <span className="tw-text-gray-500">
                              {isExpanded ? "▼" : "▶"}
                            </span>
                          </td>
                          <td className="tw-px-4 tw-py-2">
                            <a
                              href={`/inspections/${item.policyNo}/process/pre_r`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="tw-text-primary hover:tw-underline"
                            >
                              {item.policyNo}
                            </a>
                          </td>
                          <td className="tw-px-4 tw-py-2 tw-text-sm">{item.address || "—"}</td>
                          <td className="tw-px-4 tw-py-2 tw-text-sm">{item.inspectorName || "—"}</td>
                          <td className="tw-px-4 tw-py-2">
                            <span className={`tw-font-bold ${scoreColor(item.overallScore)}`}>
                              {(item.overallScore || 0).toFixed(1)}
                            </span>
                          </td>
                          <td className="tw-px-4 tw-py-2">
                            {item.autoFail ? 
                              <span className="tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-bold tw-bg-red-600 tw-text-white">
                                AUTO-FAIL
                              </span>
                              : 
                              <span className="tw-text-gray-400">—</span>
                            }
                          </td>
                          <td className="tw-px-4 tw-py-2">
                            {item.needsReview ? 
                              <span className="tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-semibold tw-bg-orange-100 tw-text-orange-800">
                                Needs Review
                              </span>
                              : 
                              <span className="tw-text-gray-400">—</span>
                            }
                          </td>
                          <td className="tw-px-4 tw-py-2">{statusBadge(item.qaStatus)}</td>
                          <td className="tw-px-4 tw-py-2">
                            <div className="tw-flex tw-gap-2">
                              <a
                                href={`/inspections/${item.policyNo}/process/pre_r`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="tw-px-3 tw-py-1 tw-rounded tw-text-xs tw-font-semibold tw-bg-primary tw-text-white hover:tw-opacity-80"
                              >
                                Open
                              </a>
                              <button
                                onClick={() => openCompleteDialog(item.policyNo)}
                                className="tw-px-3 tw-py-1 tw-rounded tw-text-xs tw-font-semibold tw-bg-fs-dark-blue tw-text-white hover:tw-opacity-80"
                              >
                                Complete QA
                              </button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && 
                          <tr className={i % 2 ? "tw-bg-gray-50" : "tw-bg-gray-50"}>
                            <td colSpan="9" className="tw-px-8 tw-py-4">
                              {isLoading && 
                                <div className="tw-flex tw-justify-center tw-py-4">
                                  <CircularProgress size={24} />
                                </div>
                              }
                              {!isLoading && detail && 
                                <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-6">
                                  {/* Categories */}
                                  <div>
                                    <h4 className="tw-font-bold tw-mb-2">Score Breakdown</h4>
                                    {detail.categories && detail.categories.length > 0 ? 
                                      <div className="tw-space-y-2">
                                        {detail.categories.map((cat, ci) => 
                                          <div key={ci} className="tw-flex tw-justify-between tw-text-sm">
                                            <span>{cat.name}</span>
                                            <span className={scoreColor(cat.score)}>
                                              {cat.score.toFixed(1)}/{(cat.maxScore || 5).toFixed(0)}
                                            </span>
                                          </div>
                                        )}
                                        {detail.categories.map((cat, ci) =>
                                          cat.findings ? 
                                            <div key={`f-${ci}`} className="tw-text-xs tw-text-gray-600 tw-pl-4">
                                              {cat.findings}
                                            </div>
                                            : null
                                        )}
                                      </div>
                                      : 
                                      <span className="tw-text-gray-400 tw-text-sm">No category data</span>
                                    }
                                  </div>

                                  {/* Vision Findings */}
                                  <div>
                                    <h4 className="tw-font-bold tw-mb-2">Vision Findings</h4>
                                    {detail.visionFindings && detail.visionFindings.length > 0 ? 
                                      <div className="tw-space-y-2">
                                        {detail.visionFindings.map((vf, vi) => 
                                          <div key={vi} className="tw-border tw-border-gray-200 tw-rounded tw-p-2 tw-text-sm">
                                            <div className="tw-flex tw-justify-between">
                                              <span className="tw-font-semibold">{vf.type}</span>
                                              <span className={`tw-text-xs tw-font-semibold ${
                                                vf.severity === "high" ? "tw-text-red-600" :
                                                  vf.severity === "medium" ? "tw-text-orange-600" :
                                                    "tw-text-yellow-600"
                                              }`}>
                                                {vf.severity}
                                              </span>
                                            </div>
                                            <div className="tw-text-gray-700">{vf.finding}</div>
                                            {vf.detail && 
                                              <div className="tw-text-xs tw-text-gray-500 tw-mt-1">{vf.detail}</div>
                                            }
                                          </div>
                                        )}
                                      </div>
                                      : 
                                      <span className="tw-text-gray-400 tw-text-sm">No vision findings</span>
                                    }
                                  </div>

                                  {/* Changed Values */}
                                  <div>
                                    <h4 className="tw-font-bold tw-mb-2">Changed Values</h4>
                                    {detail.changedValues && detail.changedValues.length > 0 ? 
                                      <div className="tw-space-y-2">
                                        {detail.changedValues.map((cv, cvi) => 
                                          <div key={cvi} className="tw-border tw-border-gray-200 tw-rounded tw-p-2 tw-text-sm">
                                            <div className="tw-flex tw-justify-between tw-items-center">
                                              <span className="tw-font-semibold">{cv.field}</span>
                                              {cv.flagged ? 
                                                <span className="tw-text-xs tw-font-semibold tw-text-red-600">⚠ Flagged</span>
                                                : 
                                                <button
                                                  className="tw-text-xs tw-px-3 tw-py-1 tw-rounded tw-bg-yellow-500 tw-text-white tw-border tw-border-yellow-600 tw-shadow-sm hover:tw-bg-yellow-600 hover:tw-shadow-md tw-transition-all"
                                                  disabled={flaggingField[`${item.policyNo}:${cv.field}`]}
                                                  onClick={() => flagField(item.policyNo, cv.field)}
                                                >
                                                  {flaggingField[`${item.policyNo}:${cv.field}`] ? "Flagging..." : "Flag"}
                                                </button>
                                              }
                                            </div>
                                            <div className="tw-text-gray-600 tw-text-xs">
                                              <span className="tw-line-through">{cv.oldValue}</span>
                                              {" → "}
                                              <span className="tw-font-semibold">{cv.newValue}</span>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                      : 
                                      <span className="tw-text-gray-400 tw-text-sm">No changed values</span>
                                    }
                                  </div>

                                  {/* Suggested edits */}
                                  {detail.suggestedEdits && detail.suggestedEdits.length > 0 &&
                                    <div className="lg:tw-col-span-3">
                                      <h4 className="tw-font-bold tw-mb-2 tw-text-blue-700">Suggested Edits</h4>
                                      <div className="tw-space-y-2">
                                        {detail.suggestedEdits.map((edit, ei) => {
                                          const st = editStateFor(item.policyNo, edit);
                                          return (
                                            <div key={ei} className="tw-flex tw-items-start tw-gap-2 tw-border tw-border-blue-200 tw-rounded tw-p-2 tw-text-sm">
                                              <input
                                                type="checkbox"
                                                checked={st.accepted}
                                                onChange={(e) => setEditState(item.policyNo, edit.answerKey, { accepted: e.target.checked })}
                                                className="tw-mt-1"
                                              />
                                              <div className="tw-flex-1">
                                                <div className="tw-flex tw-justify-between">
                                                  <span className="tw-font-semibold">{edit.answerKey}</span>
                                                  <span className="tw-text-xs tw-text-gray-400">{edit.source}</span>
                                                </div>
                                                <div className="tw-text-xs tw-text-gray-600 tw-mb-1">
                                                  <span className="tw-line-through">{edit.currentValue || "(blank)"}</span>
                                                  {" → "}
                                                  <input
                                                    type="text"
                                                    value={st.value}
                                                    onChange={(e) => setEditState(item.policyNo, edit.answerKey, { value: e.target.value })}
                                                    className="tw-border tw-border-gray-300 tw-rounded tw-px-2 tw-py-0.5 tw-text-sm tw-w-48"
                                                  />
                                                </div>
                                                <div className="tw-text-xs tw-text-gray-500">{edit.reason}</div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                        <div className="tw-mt-2">
                                          <button
                                            onClick={() => applyEdits(item.policyNo)}
                                            disabled={applyingEdits[item.policyNo]}
                                            className="tw-px-3 tw-py-1 tw-rounded tw-text-xs tw-font-semibold tw-bg-blue-600 tw-text-white hover:tw-opacity-80 disabled:tw-opacity-50"
                                          >
                                            {applyingEdits[item.policyNo] ? "Applying..." : `Apply ${detail.suggestedEdits.filter((e) => editStateFor(item.policyNo, e).accepted).length} accepted edits`}
                                          </button>
                                          <span className="tw-text-xs tw-text-gray-500 tw-ml-2">writes to inspection answers + logs history</span>
                                        </div>
                                      </div>
                                    </div>
                                  }

                                  {/* Auto-fail reasons */}
                                  {detail.autoFail && detail.autoFailReasons && 
                                    <div className="lg:tw-col-span-3">
                                      <h4 className="tw-font-bold tw-mb-2 tw-text-red-700">Auto-Fail Reasons</h4>
                                      <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded tw-p-3 tw-text-sm tw-text-red-800">
                                        {detail.autoFailReasons}
                                      </div>
                                    </div>
                                  }

                                  {/* Review reasons */}
                                  {detail.needsReview && detail.reviewReasons && 
                                    <div className="lg:tw-col-span-3">
                                      <h4 className="tw-font-bold tw-mb-2 tw-text-orange-700">Review Reasons</h4>
                                      <div className="tw-bg-orange-50 tw-border tw-border-orange-200 tw-rounded tw-p-3 tw-text-sm tw-text-orange-800">
                                        {detail.reviewReasons}
                                      </div>
                                    </div>
                                  }

                                  {/* Previous overrides */}
                                  {detail.overrides && detail.overrides.length > 0 && 
                                    <div className="lg:tw-col-span-3">
                                      <h4 className="tw-font-bold tw-mb-2">Previous QA Reviews</h4>
                                      <div className="tw-space-y-2">
                                        {detail.overrides.map((o, oi) => 
                                          <div key={oi} className="tw-border tw-border-gray-200 tw-rounded tw-p-2 tw-text-sm">
                                            <div className="tw-flex tw-justify-between">
                                              <span className="tw-font-semibold">
                                                {o.reviewerName} — {o.qaResult === "pass" ? "✅ Pass" : "❌ Fail"}
                                              </span>
                                              <span className="tw-text-xs tw-text-gray-500">
                                                {new Date(o.createdAt).toLocaleString()}
                                              </span>
                                            </div>
                                            {o.feedbackType && 
                                              <div className="tw-text-xs tw-text-gray-600">Type: {o.feedbackType}</div>
                                            }
                                            {o.feedbackText && 
                                              <div className="tw-text-gray-700 tw-mt-1">{o.feedbackText}</div>
                                            }
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  }

                                  {/* Rubric version + token usage */}
                                  {detail.rubricVersion && 
                                    <div className="lg:tw-col-span-3 tw-text-xs tw-text-gray-400">
                                      Rubric version: {detail.rubricVersion} · Scored: {new Date(detail.createdAt).toLocaleString()}
                                      {(detail.promptTokens || detail.completionTokens) > 0 &&
                                        <> · Tokens: {detail.promptTokens || 0} in / {detail.completionTokens || 0} out{typeof detail.costUsd === "number" && detail.costUsd > 0 ? ` · $${detail.costUsd.toFixed(4)}` : ""}</>
                                      }
                                    </div>
                                  }
                                </div>
                              }
                              {!isLoading && !detail && 
                                <div className="tw-text-gray-500 tw-text-sm">Failed to load detail</div>
                              }
                            </td>
                          </tr>
                        }
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          }

          {/* Pagination */}
          {total > rowsPerPage && 
            <div className="tw-flex tw-justify-center tw-mt-6 tw-gap-4">
              <button
                className="tw-px-4 tw-py-2 tw-rounded tw-border tw-border-gray-300 disabled:tw-opacity-50"
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>
              <span className="tw-self-center tw-text-sm">
                Page {page + 1} of {Math.ceil(total / rowsPerPage)}
              </span>
              <button
                className="tw-px-4 tw-py-2 tw-rounded tw-border tw-border-gray-300 disabled:tw-opacity-50"
                disabled={(page + 1) * rowsPerPage >= total}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          }
        </>
      }

      {/* Complete QA Dialog */}
      <Dialog open={!!completeDialog} onClose={closeCompleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Complete QA Review — {completeDialog}
        </DialogTitle>
        <DialogContent>
          <div className="tw-space-y-4 tw-mt-2">
            <div>
              <label className="tw-block tw-text-sm tw-font-semibold tw-mb-2">QA Result</label>
              <div className="tw-flex tw-gap-3">
                <button
                  className={`tw-px-6 tw-py-2 tw-rounded-lg tw-font-semibold ${
                    qaResult === "pass"
                      ? "tw-bg-green-600 tw-text-white"
                      : "tw-bg-gray-200 tw-text-gray-700"
                  }`}
                  onClick={() => setQaResult("pass")}
                >
                  ✅ Pass
                </button>
                <button
                  className={`tw-px-6 tw-py-2 tw-rounded-lg tw-font-semibold ${
                    qaResult === "fail"
                      ? "tw-bg-red-600 tw-text-white"
                      : "tw-bg-gray-200 tw-text-gray-700"
                  }`}
                  onClick={() => setQaResult("fail")}
                >
                  ❌ Fail
                </button>
              </div>
            </div>

            <FormControl fullWidth>
              <InputLabel>Feedback Type</InputLabel>
              <Select
                value={feedbackType}
                onChange={(e) => setFeedbackType(e.target.value)}
              >
                {FEEDBACK_TYPES.map((ft) => 
                  <MenuItem key={ft} value={ft}>{ft}</MenuItem>
                )}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Feedback for AI Scorer"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="What did the AI get right or wrong? What did it miss?"
              variant="outlined"
            />

            <div className="tw-text-xs tw-text-gray-500">
              Note: This will NOT submit the inspection to the insurance system. It only marks the QA status.
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCompleteDialog} color="default">
            Cancel
          </Button>
          <Button
            onClick={submitQA}
            color="primary"
            variant="contained"
            disabled={!qaResult || submitting}
          >
            {submitting ? <CircularProgress size={20} /> : "Submit QA Review"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default QAReview;